import React, {useState, useEffect} from "react";
import {useSelector, useDispatch} from "react-redux";
import useOnClickOutside from "use-onclickoutside";

import * as actionTypes from "../../../store/actions/actionTypes";
import {finishUploadPdf, removeUploadedFile} from "../../../store/actions/customUI";
import Admission_ModalInfo from "../../Block/Admission_ModalInfo";
import AdmissionBulkDocumentsPdf from "../../AdmissionBulkDocumentsPdf";
import FileUploader2 from "../../FileUploader2";
import FileUploaderMultiple from "../../FileUploaderMultiple";
import Spinner from "../../Spinner";
import UploadPdf_SelectBooking from "./UploadPdf_SelectBooking";


const UploadPdf = () => {
    const [fileId, setFileId] = useState(null);
    const [approving, setApproving] = useState(false);
    const [showPrint, setShowPrint] = useState(false);
    // Multiple-uploads mode state.
    const [pendingFileIds, setPendingFileIds] = useState([]);
    const [files, setFiles] = useState([]);
    const [uploaderKey, setUploaderKey] = useState(0);
    const [previewFile, setPreviewFile] = useState(null);
    const {show, admission, customUI} = useSelector(state => state.modal.customUI_uploadPdf);
    const ref = React.useRef(null);
    const dispatch = useDispatch();

    const isMultiple = !!customUI.multipleUploads;

    const hideModal = () => dispatch({type: actionTypes.MODAL_CUSTOM_UI_UPLOAD_PDF_HIDE});
    useOnClickOutside(ref, () => hideModal());

    // IMP-767 - reset the print view whenever the modal is reopened.
    useEffect(() => {
        if (!show) setShowPrint(false);
    }, [show]);

    // Seed the uploaded-files list from the admission snapshot each time the
    // modal opens in multiple-uploads mode.
    useEffect(() => {
        if (show && isMultiple) {
            const status = admission.manager_customUI && admission.manager_customUI[customUI.id];
            setFiles(status && status.files ? status.files : []);
            setPendingFileIds([]);
            setUploaderKey(prev => prev + 1);
            setPreviewFile(null);
        }
    }, [show, isMultiple, admission, customUI.id]);

    // IMP-767 - per-hospital toggle: a "Print" button that generates the configured document
    // (e.g. surgical consent form) pre-filled with the admission's data, without saving it.
    const printLabel = customUI.pdfGenerator_btnTitle;
    const generatePdfAction = `${process.env.REACT_APP_API_PATH}/admission/${admission.id}/custom-ui/${customUI.id}/generate-pdf`;

    const doApprove = () => {
        dispatch(finishUploadPdf(admission, customUI, fileId));
        hideModal();
    }

    // Finalise every pending temporary upload, then refresh the file list from
    // the last response and reset the uploader so more files can be added.
    const doApproveMultiple = async () => {
        setApproving(true);
        try {
            let updatedAdmission = null;
            for (const pendingId of pendingFileIds) {
                updatedAdmission = await dispatch(finishUploadPdf(admission, customUI, pendingId));
            }
            refreshFilesFrom(updatedAdmission);
            setPendingFileIds([]);
            setUploaderKey(prev => prev + 1);
        } finally {
            // Always release the UI, even if a finalise request fails, so the
            // modal never gets stuck with disabled buttons.
            setApproving(false);
        }
    }

    const refreshFilesFrom = (updatedAdmission) => {
        if (!updatedAdmission) return;
        const status = updatedAdmission.manager_customUI && updatedAdmission.manager_customUI[customUI.id];
        setFiles(status && status.files ? status.files : []);
    }

    const doRemoveFile = async (removeFileId) => {
        setApproving(true);
        try {
            const updatedAdmission = await dispatch(removeUploadedFile(admission, customUI, removeFileId));
            refreshFilesFrom(updatedAdmission);
        } finally {
            // Always release the UI, even if the remove request fails.
            setApproving(false);
        }
    }

    const uploadTitle = customUI.uploadTitle ? customUI.uploadTitle : `Upload ${customUI.managerTitle}`;

    const renderUploadedFiles = () => (
        <div className="form-group">
            <h4>Uploaded files</h4>
            {files.length === 0
                ? <p><em>No files uploaded yet.</em></p>
                : <ul className="list-group mb-3">
                    {files.map(file => (
                        <li key={file.id} className="list-group-item d-flex justify-content-between align-items-center">
                            <span>
                                {file.originalName}
                                {file.uploadedAt && <small className="text-muted"> — {file.uploadedAt}</small>}
                            </span>
                            <span>
                                <button type="button" className="btn btn-sm btn-outline-primary mr-2"
                                        onClick={() => setPreviewFile({id: file.id, name: file.originalName})}>View</button>
                                <button type="button" className="btn btn-sm btn-outline-danger" disabled={approving}
                                        onClick={() => doRemoveFile(file.id)}>Remove</button>
                            </span>
                        </li>
                    ))}
                </ul>
            }
        </div>
    );

    const renderMultipleForm = () => {
        // Inline PDF preview (reuses AdmissionBulkDocumentsPdf, which fetches the
        // file as a blob and renders it in an iframe with Print/Download).
        if (previewFile) {
            return (
                <>
                    <div className="d-flex justify-content-between align-items-center">
                        <h4>Preview - {previewFile.name}</h4>
                        <button type="button" className="btn btn-outline-primary" onClick={() => setPreviewFile(null)}>
                            Back to the list
                        </button>
                    </div>
                    <AdmissionBulkDocumentsPdf
                        admissionId={admission.id}
                        action={`${process.env.REACT_APP_API_PATH}/admission/${admission.id}/custom-ui/${customUI.id}/download-file/${previewFile.id}`}
                        title={previewFile.name} downloadName={previewFile.name} width="100%"/>
                </>
            );
        }

        return (
        <>
            {customUI.bookingLink_enable &&
                <UploadPdf_SelectBooking admission={admission} customUI={customUI} onFileLinked={refreshFilesFrom}/>}
            <form className="person-info-form">
                {renderUploadedFiles()}
                <h4>Add files</h4>
                <FileUploaderMultiple
                    api={`${process.env.REACT_APP_API_PATH}/admission/${admission.id}/upload-pdf2`}
                    onChange={setPendingFileIds}
                    resetKey={uploaderKey}
                />
                <div className="form-group">
                    <div className="row">
                        <div className="col-12 d-flex justify-content-end">
                            <input type="button" value="Close" className="btn" onClick={hideModal}/>
                            <input type="button" value="Approve & Upload" className="btn btn-primary"
                                   disabled={pendingFileIds.length === 0 || approving} onClick={doApproveMultiple}/>
                        </div>
                    </div>
                </div>
            </form>
        </>
        );
    };

    const renderSingleForm = () => (
        <>
            {customUI.bookingLink_enable &&
                <UploadPdf_SelectBooking admission={admission} customUI={customUI}/>}
            <form className="person-info-form">
                {customUI.bookingLink_enable && <h4>Upload PDF</h4>}
                {approving ? <Spinner /> : <FileUploader2 api={`${process.env.REACT_APP_API_PATH}/admission/${admission.id}/upload-pdf2`} onSetFile={setFileId}/>}
                <div className="form-group">
                    <div className="row">
                        <div className="col-12 d-flex justify-content-end">
                            <input type="button" value="Cancel" className="btn" onClick={hideModal}/>
                            <input type="button" value="Approve & Upload" className="btn btn-primary" disabled={!fileId || approving} onClick={doApprove}/>
                        </div>
                    </div>
                </div>
            </form>
        </>
    );

    const renderModal = () => {
        if (show) {
            return (
                <div className="modal-dialog modal-content-large" role="document" ref={ref} autoFocus={false}>
                    <div className="modal-content modal-content-fit">
                        <button type="button" className="close" data-dismiss="modal" aria-label="Close"
                                onClick={hideModal}>
                            <span aria-hidden="true">×</span>
                        </button>
                        <div className="modal-body">
                            <div className="d-flex justify-content-between align-items-start">
                                <h3>{uploadTitle} - {admission.title} {admission.surname}, {admission.name}</h3>
                                {customUI.pdfGenerator_enable &&
                                <button type="button" className="btn btn-outline-primary"
                                        style={{marginRight: '2rem'}}
                                        onClick={() => setShowPrint(prev => !prev)}>
                                    {showPrint ? 'Back to upload' : printLabel}
                                </button>
                                }
                            </div>
                            <Admission_ModalInfo admission={admission} showMobileInfo={false} />
                            {showPrint
                                ? <AdmissionBulkDocumentsPdf admissionId={admission.id} action={generatePdfAction}
                                                             title={printLabel}
                                                             downloadName={`admission_${admission.id}.pdf`} width="100%"/>
                                : (isMultiple ? renderMultipleForm() : renderSingleForm())
                            }
                        </div>
                    </div>
                </div>
            );
        }
        return null;
    }

    return (
        <>
            <div className={`modal fade ${show ? 'show' : ''}`} id="personModal" tabIndex="-1" role="dialog"
                 aria-labelledby="personModalLabel" aria-modal="true">
                {renderModal()}
            </div>
        </>
    );
}

export default UploadPdf;
